import { Link } from '@tanstack/react-router';
import { Plus, Target, ChevronRight } from 'lucide-react';
import { cn, Button } from '@jeraisonne/ui';
import { useAdminObjectiveTemplates } from '@/lib/admin/objective-queries';
import type { AdminObjectiveTemplate } from '@/lib/admin/objective-queries';
import { useUpdateObjectiveTemplate } from '@/lib/admin/objective-mutations';

const OBJECTIVE_TYPE_LABEL: Record<string, string> = {
  full_program: 'Tout le programme',
  chapter_review: 'Réviser un chapitre',
  current_chapter: 'Chapitre en cours',
  weak_spots: 'Mes faiblesses',
  warmup: 'Échauffement (chapitre)',
  diagnostic: 'Diagnostic (auto, gratuit)',
  brevet_revision: 'Révision brevet',
  brevet_blanc: 'Brevet blanc',
  bac_revision: 'Révision bac',
  bac_blanc: 'Bac blanc',
};

function ObjectiveRow({ template }: { template: AdminObjectiveTemplate }) {
  const update = useUpdateObjectiveTemplate();

  function toggleActive() {
    update.mutate({ id: template.id, is_active: !template.is_active });
  }

  return (
    <div className="hover:bg-ink-50 group flex items-center gap-3 rounded-xl p-3 transition-colors">
      <Link
        to="/tutor/admin/objectives/$objectiveId"
        params={{ objectiveId: template.id }}
        className="flex min-w-0 flex-1 items-center gap-3"
      >
        {template.illustration_url ? (
          <img
            src={template.illustration_url}
            alt=""
            loading="lazy"
            aria-hidden="true"
            className="border-ink-100 h-12 w-12 shrink-0 rounded-lg border object-cover"
          />
        ) : (
          <div className="bg-primary-50 flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
            <Target className="text-primary-500 h-5 w-5" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-display text-ink-900 truncate text-[14px] font-semibold">
            {template.title}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <span className="text-ink-500 font-sans text-[11px]">
              {OBJECTIVE_TYPE_LABEL[template.type] ?? template.type}
            </span>
            {template.content_type === 'epreuves' && (
              <>
                <span className="text-ink-300">·</span>
                <span className="bg-level-50 text-level-600 rounded-full px-1.5 py-0 font-sans text-[10px] font-semibold">
                  Épreuves
                </span>
              </>
            )}
            {template.requires_pack_selection && (
              <>
                <span className="text-ink-300">·</span>
                <span className="text-ink-400 font-sans text-[10px]">Choix du pack</span>
              </>
            )}
            {(template.student_count ?? 0) > 0 && (
              <>
                <span className="text-ink-300">·</span>
                <span className="text-ink-400 font-sans text-[11px]">
                  {template.student_count} élève{(template.student_count ?? 0) > 1 ? 's' : ''}
                </span>
              </>
            )}
          </div>
        </div>
        <span
          className={cn(
            'font-display shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold',
            template.is_active ? 'bg-xp-100 text-xp-700' : 'bg-ink-100 text-ink-600',
          )}
        >
          {template.is_active ? 'Actif' : 'Inactif'}
        </span>
        <ChevronRight className="text-ink-300 group-hover:text-ink-500 h-4 w-4 shrink-0 transition-colors" />
      </Link>
      <button
        onClick={toggleActive}
        disabled={update.isPending}
        className="text-primary-600 hover:text-primary-800 shrink-0 font-sans text-[11px] underline transition-opacity disabled:opacity-50"
      >
        {template.is_active ? 'Désactiver' : 'Activer'}
      </button>
    </div>
  );
}

export function TutorAdminObjectivesPage() {
  const { data: templates = [], isLoading } = useAdminObjectiveTemplates();

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-ink-900 text-[22px] font-extrabold">Objectifs</h1>
          <p className="text-ink-500 mt-0.5 font-sans text-[13px]">
            {templates.length} template{templates.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Link to="/tutor/admin/objectives/new">
          <Button size="sm" className="flex items-center gap-1.5">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Nouveau template</span>
            <span className="sm:hidden">Nouveau</span>
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : templates.length === 0 ? (
        <div className="py-16 text-center">
          <Target className="text-ink-300 mx-auto mb-3 h-10 w-10" />
          <p className="font-display text-ink-600 font-semibold">Aucun template</p>
        </div>
      ) : (
        <div className="border-ink-200 divide-ink-100 divide-y overflow-hidden rounded-2xl border bg-white">
          {templates
            .slice()
            .sort((a, b) => a.display_order - b.display_order)
            .map((t) => (
              <ObjectiveRow key={t.id} template={t} />
            ))}
        </div>
      )}
    </div>
  );
}
